/**
 * @fileoverview Hook for filtering and computing student lists
 */
import { useMemo } from 'react'
import type { Student } from './types'

interface UseStudentFiltersParams {
  students: Student[]
  assignedStudentIds: string[]
  searchQuery: string
  filterGroupId: string | null
}

interface UseStudentFiltersReturn {
  availableStudents: Student[]
  assignedStudents: Student[]
  studentsMap: Map<string, Student>
  totalInGroup: number
}

const filterBySearch = (students: Student[], query: string): Student[] => {
  if (!query.trim()) return students
  const normalizedQuery = query.toLowerCase().trim()
  return students.filter(s =>
    `${s.firstName} ${s.lastName}`.toLowerCase().includes(normalizedQuery)
  )
}

export const useStudentFilters = ({
  students,
  assignedStudentIds,
  searchQuery,
  filterGroupId,
}: UseStudentFiltersParams): UseStudentFiltersReturn => {
  const studentsMap = useMemo(
    () => new Map(students.map(s => [s.id, s])),
    [students],
  )

  // Use Set for O(1) lookup instead of O(n) with includes()
  const assignedSet = useMemo(
    () => new Set(assignedStudentIds),
    [assignedStudentIds],
  )

  const studentsInGroup = useMemo(() => {
    if (!filterGroupId) return students
    return students.filter(s => s.groupIds.includes(filterGroupId))
  }, [students, filterGroupId])

  const availableStudents = useMemo(() => {
    const result = studentsInGroup.filter(s => !assignedSet.has(s.id))
    return filterBySearch(result, searchQuery)
  }, [studentsInGroup, assignedSet, searchQuery])

  const assignedStudents = useMemo(
    () => assignedStudentIds
      .map(id => studentsMap.get(id))
      .filter((s): s is Student => s !== undefined),
    [assignedStudentIds, studentsMap],
  )

  return { availableStudents, assignedStudents, studentsMap, totalInGroup: studentsInGroup.length }
}
